import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ElliottWorking from '../../../../../components/ui/ElliottWorking';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useAIMDContext } from '../context/AIMDContext';
import { useBuildLabels } from '../buildLabels';
import {
  roleplayFlags,
  savedRoleplayFlags,
  uncoveredCapabilities,
  useLessonTags,
  withLessonTag,
  type LessonTagChoice
} from '../lessonTags';
import {
  capabilitiesLeftWithoutLesson,
  capabilityLostByRetag,
  lastLessonWarning,
  lessonsOfModule
} from '../capabilityGuards';
import CapabilityConfirm, { toastWithUndo, type CapabilityConfirmRequest } from '../components/CapabilityConfirm';
import { useBuilderWritable } from '../../../../../components/program-collaboration';
import StepInviteAdmin from '../components/StepInviteAdmin';
import { ModuleStructure, ProgramStructure } from '../types';
import ModuleSidebar from '../components/ModuleSidebar';
import AIReasoningModal from '../components/AIReasoningModal';
import ImproveWithAIModal from '../components/ImproveWithAIModal';
import LessonTagLine from '../components/LessonTagLine';

const sectionTitleSx = {
  fontSize: 16,
  fontWeight: 600,
  fontFamily: 'Inter',
  color: 'var(--Text-text-primary, #FDFDFD)',
  mb: 1,
  mt: 3
};
const guideListSx = { display: 'flex', flexDirection: 'column', gap: 0.75 };
const guideIconSx = { fontSize: 14, color: 'var(--Stroke-highlight, #F79420)' };
const bulletSx = { width: 4, height: 4, borderRadius: '50%', bgcolor: 'var(--Text-text-secondary, #B6B6B6)' };

/** One line of the guide: a fixed marker column, so bullets and icons line up under the heading. */
const GuideRow = ({ marker, children }: { marker: React.ReactNode; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ width: 14, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {marker}
    </Box>
    <Typography
      sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: '20px' }}
    >
      {children}
    </Typography>
  </Box>
);

/** "A", "A and B", "A, B and C". */
const listed = (names: string[]) =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

const StructureStep: React.FC = () => {
  const ctx = useAIMDContext();
  const { structureTitle } = useBuildLabels();
  // The Capability build flow: what each lesson is for (null in the standalone wizard).
  const lessonTags = useLessonTags();
  const {
    state, generationPhase, generationError, embedded,
    handleGenerateStructure, handleImproveModule, updateStructure, updateRequirements
  } = ctx;

  const structure = state.structure.generatedStructure as ProgramStructure | null;
  const modules = structure?.modules || [];
  const isLoading = generationPhase === 'structure';

  const [selectedModuleIdx, setSelectedModuleIdx] = useState<number | null>(null);
  const [selectedLessonIdx, setSelectedLessonIdx] = useState<number | null>(null);
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const [reasoningModuleIdx, setReasoningModuleIdx] = useState(0);
  const [improveModuleOpen, setImproveModuleOpen] = useState(false);
  const [improveModuleIdx, setImproveModuleIdx] = useState(0);
  // Asked before an edit that leaves a capability with no lesson (docs/PRD-capability-lesson-roleplays.md R5).
  const [confirm, setConfirm] = useState<CapabilityConfirmRequest | null>(null);


  const hasTriggered = useRef(false);
  // Only the admin holding the edit lock may kick off generation: not a
  // read-only viewer, and not before the lock answer is back (the copy on
  // screen may be stale and the other admin may already be generating).
  const writable = useBuilderWritable();

  useEffect(() => {
    if (!writable) return;
    if (!structure && !isLoading && !hasTriggered.current) {
      hasTriggered.current = true;
      handleGenerateStructure();
    }
  }, [structure, isLoading, handleGenerateStructure, writable]);

  const handleReorder = useCallback((updated: ModuleStructure[]) => {
    updateStructure({ generatedStructure: { modules: updated } });
  }, [updateStructure]);

  const handleRenameModule = useCallback((idx: number, title: string) => {
    if (!structure) return;
    const updated = [...structure.modules];
    updated[idx] = { ...updated[idx], title };
    updateStructure({ generatedStructure: { modules: updated } });
  }, [structure, updateStructure]);

  const handleRenameLesson = useCallback((mIdx: number, lIdx: number, title: string) => {
    if (!structure) return;
    const updated = structure.modules.map((m, i) => {
      if (i !== mIdx) return m;
      const lessons = [...m.lessons];
      lessons[lIdx] = { ...lessons[lIdx], title };
      return { ...m, lessons };
    });
    updateStructure({ generatedStructure: { modules: updated } });
  }, [structure, updateStructure]);

  const handleAddModule = useCallback(() => {
    if (!structure) return;
    const newModule: ModuleStructure = {
      title: `New Module ${structure.modules.length + 1}`,
      reasoning: '',
      learningOutcomesServed: [],
      ragDocumentTitles: [],
      lessons: [{ title: 'New Lesson 1', reasoning: '' }]
    };
    updateStructure({ generatedStructure: { modules: [...structure.modules, newModule] } });
  }, [structure, updateStructure]);

  const handleAddLesson = useCallback((mIdx: number) => {
    if (!structure) return;
    const updated = structure.modules.map((m, i) => {
      if (i !== mIdx) return m;
      return { ...m, lessons: [...m.lessons, { title: `New Lesson ${m.lessons.length + 1}`, reasoning: '' }] };
    });
    updateStructure({ generatedStructure: { modules: updated } });
  }, [structure, updateStructure]);

  // A delete restores in place with Undo; one that leaves a capability with no lesson asks first (R5).
  const deleteWithUndo = useCallback((updated: ModuleStructure[], message: string) => {
    if (!structure) return;
    const before = structure.modules;
    updateStructure({ generatedStructure: { modules: updated } });
    toastWithUndo(message, () => updateStructure({ generatedStructure: { modules: before } }));
  }, [structure, updateStructure]);

  const handleDeleteModule = useCallback((idx: number) => {
    if (!structure || structure.modules.length <= 1) return;
    const run = () => {
      deleteWithUndo(structure.modules.filter((_, i) => i !== idx), 'Module deleted.');
      if (selectedModuleIdx === idx) { setSelectedModuleIdx(null); setSelectedLessonIdx(null); }
    };
    const lost = lessonTags ? capabilitiesLeftWithoutLesson(structure.modules, lessonTags, lessonsOfModule(structure.modules, idx)) : [];
    if (lost.length === 0) return run();
    setConfirm({ title: 'Delete this module?', body: lastLessonWarning(lost, 'module'), confirmLabel: 'Delete anyway', onConfirm: run });
  }, [structure, lessonTags, deleteWithUndo, selectedModuleIdx]);

  const handleDeleteLesson = useCallback((mIdx: number, lIdx: number) => {
    if (!structure) return;
    const mod = structure.modules[mIdx];
    if (mod.lessons.length <= 1) return;
    const run = () => {
      const updated = structure.modules.map((m, i) => {
        if (i !== mIdx) return m;
        return { ...m, lessons: m.lessons.filter((_, j) => j !== lIdx) };
      });
      deleteWithUndo(updated, 'Lesson deleted.');
      if (selectedModuleIdx === mIdx && selectedLessonIdx === lIdx) setSelectedLessonIdx(null);
    };
    const lost = lessonTags
      ? capabilitiesLeftWithoutLesson(structure.modules, lessonTags, [{ moduleIndex: mIdx, lessonIndex: lIdx }])
      : [];
    if (lost.length === 0) return run();
    setConfirm({ title: 'Delete this lesson?', body: lastLessonWarning(lost), confirmLabel: 'Delete anyway', onConfirm: run });
  }, [structure, lessonTags, deleteWithUndo, selectedModuleIdx, selectedLessonIdx]);

  const handleReasoningClick = useCallback((mIdx: number) => {
    setReasoningModuleIdx(mIdx);
    setReasoningOpen(true);
  }, []);

  const handleImproveModuleClick = useCallback((mIdx: number) => {
    setImproveModuleIdx(mIdx);
    setImproveModuleOpen(true);
  }, []);

  const handleImproveModuleSubmit = useCallback(async (feedback: string) => {
    await handleImproveModule(improveModuleIdx, feedback);
    setImproveModuleOpen(false);
  }, [handleImproveModule, improveModuleIdx]);

  // The build flow: where the graded Roleplays fall, and any capability left with no lesson. While the
  // structure is still a plan they follow it; once lessons are written they are the admin's, as saved (D3).
  const contentWritten = Boolean(
    (state.content.generatedContent as { modules?: Array<{ lessons?: Array<{ content?: string }> }> } | null)?.modules?.some(
      (m) => m.lessons?.some((l) => Boolean(l.content))
    )
  );
  const roleplays = useMemo(
    () =>
      lessonTags
        ? contentWritten
          ? savedRoleplayFlags(structure?.modules ?? [])
          : roleplayFlags(structure?.modules ?? [], lessonTags)
        : null,
    [structure, lessonTags, contentWritten]
  );
  const uncovered = lessonTags ? uncoveredCapabilities(modules, lessonTags) : [];
  const handleTagLesson = useCallback((mIdx: number, lIdx: number, choice: LessonTagChoice) => {
    if (!structure) return;
    const run = () =>
      updateStructure({ generatedStructure: { modules: withLessonTag(structure.modules, mIdx, lIdx, choice) } });
    const lost = lessonTags ? capabilityLostByRetag(structure.modules, lessonTags, { moduleIndex: mIdx, lessonIndex: lIdx }, choice) : null;
    if (!lost) return run();
    setConfirm({ title: 'Change what this lesson is for?', body: lastLessonWarning([lost]), confirmLabel: 'Change anyway', onConfirm: run });
  }, [structure, lessonTags, updateStructure]);

  

  // Loading state
  if (isLoading && !structure) {
    return <ElliottWorking size="full" message="Elliott is building your program structure..." />;
  }

  if (generationError && !structure) {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
        <Typography sx={{ fontSize: 16, color: 'var(--UI-Error, #F44336)', fontFamily: 'Inter' }}>{generationError}</Typography>
        <DosenButton
          tone="cta"
          sizeVariant="lg"
          onClick={() => {
            hasTriggered.current = false;
            handleGenerateStructure();
          }}
        >
          Try Again
        </DosenButton>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', gap: 3, minHeight: 0, flex: 1 }}>
      {/* Left panel */}
      <Box sx={{
        width: 320, flexShrink: 0, overflowY: 'auto',
        borderRight: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)', pr: 2
      }}>
        <ModuleSidebar
          programName={state.requirements.programName}
          modules={modules}
          selectedModuleIdx={selectedModuleIdx}
          selectedLessonIdx={selectedLessonIdx}
          onSelectModule={(idx) => { setSelectedModuleIdx(idx); setSelectedLessonIdx(null); }}
          onSelectLesson={(mIdx, lIdx) => { setSelectedModuleIdx(mIdx); setSelectedLessonIdx(lIdx); }}
          onReorder={handleReorder}
          onRenameModule={handleRenameModule}
          onRenameLesson={handleRenameLesson}
          onAddModule={handleAddModule}
          onAddLesson={handleAddLesson}
          onDeleteModule={handleDeleteModule}
          onDeleteLesson={handleDeleteLesson}
          onReasoningClick={handleReasoningClick}
          onImproveModuleClick={handleImproveModuleClick}
          onProgramNameChange={(name) => updateRequirements({ programName: name })}
          hideProgramName={embedded}
          renderLessonExtra={
            lessonTags && roleplays
              ? (mIdx, lIdx) => (
                  <LessonTagLine
                    lesson={modules[mIdx].lessons[lIdx]}
                    roleplay={Boolean(roleplays[mIdx]?.[lIdx])}
                    tags={lessonTags}
                    editable={writable}
                    onTag={(choice) => handleTagLesson(mIdx, lIdx, choice)}
                  />
                )
              : undefined
          }
        />
      </Box>

      {/* Right panel – always shows the static info */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        <Box sx={{ pt: embedded ? '16px' : 0, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 24, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}>
            {structureTitle}
          </Typography>
          <StepInviteAdmin />
        </Box>
        <Box sx={{ maxWidth: 560 }}>
          {uncovered.length > 0 && (
            <Typography
              role="alert"
              sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--UI-Error, #F44336)', lineHeight: '22px', mb: 2 }}
            >
              No lesson builds {listed(uncovered.map((c) => c.name))}. Every capability this program builds needs a
              lesson: choose one for it, or add one, before building the lessons.
            </Typography>
          )}
          {lessonTags ? (
            <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: '22px', mb: 3 }}>
              Your program structure is on the left. Each lesson shows the capability or goals the lesson covers. A
              Scored Roleplay tag marks the last lesson of each capability, and the halfway lesson when a capability has
              four or more.
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: '22px', mb: 3 }}>
              Your program structure is on the left. A program usually has 4 modules with 4 lessons in each.
            </Typography>
          )}
          <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: '22px', mb: 2 }}>
            <strong style={{ color: 'var(--Text-text-primary, #FDFDFD)' }}>
              Before Elliott writes the lessons, you can:
            </strong>
          </Typography>

          <Typography sx={sectionTitleSx}>Edit manually</Typography>
          <Box sx={guideListSx}>
            <GuideRow marker={<Box sx={bulletSx} />}>Rename or delete modules and lessons manually</GuideRow>
            <GuideRow marker={<Box sx={bulletSx} />}>Add modules or lessons manually</GuideRow>
            <GuideRow marker={<Box sx={bulletSx} />}>Reorder modules or lessons</GuideRow>
          </Box>

          <Typography sx={sectionTitleSx}>Improve with Elliott</Typography>
          <Box sx={guideListSx}>
            {/* The same icons as the module rows on the left (ModuleSidebar). */}
            <GuideRow marker={<InfoOutlinedIcon sx={guideIconSx} />}>
              See Elliott&apos;s reasoning for this structure
            </GuideRow>
            <GuideRow marker={<EditOutlinedIcon sx={guideIconSx} />}>
              Improve the structure of a specific module
            </GuideRow>
          </Box>
        </Box>
      </Box>

      {/* Modals */}
      <AIReasoningModal
        open={reasoningOpen}
        onClose={() => setReasoningOpen(false)}
        module={modules[reasoningModuleIdx] || null}
        moduleIndex={reasoningModuleIdx}
        documents={[...state.documents.uploadedDocuments, ...state.documents.previousDocuments]}
      />

      <ImproveWithAIModal
        variant="module"
        open={improveModuleOpen}
        onClose={() => setImproveModuleOpen(false)}
        onSubmit={handleImproveModuleSubmit}
        isLoading={isLoading}
        entityName={modules[improveModuleIdx]?.title}
      />

      <CapabilityConfirm request={confirm} onClose={() => setConfirm(null)} />
    </Box>
  );
};

export default StructureStep;
